if (!window.InstrumentGuides) (() => {
  const cache = new Map();
  const endpoint = () => theme.settings.instrumentGuidesEndpoint;
  const limit = () => theme.settings.instrumentGuidesLimit || 10;
  let cooldownUntil = 0;
  let requestCount = 0;

  function normalizeQuery(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function getSearchQuery(value) {
    const normalized = normalizeQuery(value).slice(0, 120);
    const tokens = normalized.split(' ').filter((token) => token.length >= 2).slice(0, 8);
    return { normalized, query: tokens.join(' ') };
  }

  function getSessionId() {
    try {
      return sessionStorage.getItem('wbp-instrument-guides-session-id');
    } catch (error) {
      return null;
    }
  }

  function setSessionId(sessionId) {
    if (!sessionId) return;
    try {
      sessionStorage.setItem('wbp-instrument-guides-session-id', sessionId);
    } catch (error) {
      return;
    }
  }

  function getCooldownRemaining() {
    return Math.max(0, cooldownUntil - Date.now());
  }

  async function search(value, signal, type = 'instrument') {
    const searchQuery = getSearchQuery(value);
    if (searchQuery.normalized.length < 3 || searchQuery.query.length < 3) {
      return { skipped: true, query: searchQuery.query, results: [] };
    }

    const searchLimit = Math.min(20, Math.max(1, Number(limit())));
    const key = `${searchQuery.query}|${type}|${searchLimit}`;
    const cached = cache.get(key);
    if (cached) return cached;

    if (getCooldownRemaining() > 0) {
      return { skipped: true, blocked: true, query: searchQuery.query, results: [] };
    }

    const body = {
      query: searchQuery.query,
      type,
      limit: searchLimit
    };
    const sessionId = getSessionId();
    if (sessionId) body.Session_ID = sessionId;

    const response = await fetch(endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal
    });
    requestCount += 1;

    if (response.status === 429) {
      const retryAfter = Number(response.headers.get('Retry-After'));
      cooldownUntil = Date.now() + (Number.isFinite(retryAfter) ? Math.max(1, retryAfter) * 1000 : 60000);
      throw new Error('rate_limited');
    }

    if (response.status === 503) throw new Error('disabled');
    if (!response.ok) throw new Error(`${type}_${response.status}`);

    const payload = await response.json();
    setSessionId(payload.Session_ID);
    const result = {
      query: searchQuery.query,
      count: Number(payload.count) || 0,
      results: Array.isArray(payload.results) ? payload.results : []
    };
    cache.set(key, result);
    return result;
  }

  function createResult(result, idPrefix, type) {
    const item = document.createElement('li');
    item.className = `predictive-search__item ${type}__item`;
    item.id = `${idPrefix}-${requestCount}-${Math.random().toString(36).slice(2, 8)}`;
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');

    const link = document.createElement('a');
    link.className = `predictive-result flex items-start focus-inset js-search-link ${type}__result`;
    link.href = typeof result.url === 'string' ? result.url : '#';
    if (theme.settings.externalLinksNewTab) link.target = '_blank';
    if (link.target === '_blank') link.rel = 'noopener';

    if (result.image_url) {
      const media = document.createElement('div');
      media.className = 'predictive-result__media media relative';
      const image = document.createElement('img');
      image.src = result.image_url;
      image.alt = result.image_alt || result.title || '';
      image.loading = 'lazy';
      media.append(image);
      link.append(media);
    }

    const info = document.createElement('div');
    info.className = 'predictive-result__info flex-auto';
    const title = document.createElement('h3');
    title.className = 'predictive-result__title h6';
    title.textContent = result.title || result.handle || (type === 'software-tools' ? 'Software Tool' : 'Instrument Guide');
    info.append(title);

    if (result.summary) {
      const summary = document.createElement('p');
      summary.className = `predictive-result__sub-title ${type}__summary`;
      summary.textContent = result.summary;
      info.append(summary);
    }

    link.append(info);
    item.append(link);
    return item;
  }

  function render(container, data, idPrefix, type = 'instrument-guides', showHeading = false) {
    container.replaceChildren();
    if (!data || data.skipped) {
      container.hidden = true;
      return 0;
    }

    if (!data.results.length) {
      container.hidden = true;
      return 0;
    }

    if (showHeading) {
      const heading = document.createElement('h2');
      heading.className = `${type}__heading`;
      heading.textContent = type === 'software-tools' ? 'Software Tools' : 'Instrument Guide';
      container.append(heading);
    }

    const list = document.createElement('ul');
    list.className = `predictive-search__list ${type}__list`;
    list.setAttribute('role', 'list');
    data.results.forEach((result, index) => {
      const item = createResult(result, `${idPrefix}-${index}`, type);
      if (index > 0 && result.matched_tier !== data.results[index - 1].matched_tier) {
        item.classList.add(`${type}__tier-break`);
      }
      list.append(item);
    });
    container.append(list);
    container.hidden = false;
    return data.results.length;
  }

  function mountPage(container, type = 'instrument-guides') {
    if (!container || !container.dataset.query) return;
    const controller = new AbortController();
    const loading = window.setTimeout(() => {
      container.setAttribute('aria-busy', 'true');
      container.textContent = 'Loading Instrument Guides';
    }, 150);
    search(container.dataset.query, controller.signal, type === 'software-tools' ? 'software_tools' : 'instrument')
      .then((data) => {
        window.clearTimeout(loading);
        render(container, data, `${type}-page`, type, true);
        container.removeAttribute('aria-busy');
      })
      .catch(() => {
        window.clearTimeout(loading);
        container.replaceChildren();
        container.hidden = true;
      });
  }

  window.InstrumentGuides = {
    search,
    render,
    mountPage,
    getSearchQuery,
    getCooldownRemaining
  };
  window.SoftwareTools = {
    search: (value, signal) => search(value, signal, 'software_tools'),
    render: (container, data, idPrefix) => render(container, data, idPrefix, 'software-tools'),
    mountPage: (container) => mountPage(container, 'software-tools')
  };
})();
